import { getTranslation } from 'helpers/getTranslation';

import {
  eventNameToTranslationKey,
  getAnalyticsEventBaseName,
} from './analyticsEventUtils';
import {
  formatAnalyticsEventDisplay,
  translateAnalyticsEvent,
} from './formatAnalyticsEventLabel';
import { normalizeEndpointTemplate } from './journeyEndpointLabels';
import {
  LOG_GAP_THRESHOLD_MS,
  findErrorRootCause,
  formatDuration,
  getErrorDiagnostics,
  getEventCatalogRedundancy,
  groupEventsByScreen,
  readJourneyCatalogId,
  readJourneyLessonIdentity,
  readJourneyModuleIdentity,
  shouldShowGapBefore,
  type AnalyticsStudentSessionDetailDto,
  type EventKind,
  type ScreenEventGroup,
  type SessionEvent,
  type SessionScreen,
} from './TimelineSection.helpers';

/*
 * The event log of one session, made readable: screens of one lesson / trainer
 * become one chapter, a task's start and its result become one row, identical
 * rows become «×3», the exit is one row, the screen's own view event moves into
 * the chapter header and non-blocking 4xx rows wait for «Технические детали».
 * Every event stays addressable: rows carry the indexes of what they absorbed,
 * so «К ошибке ↓», highlight and the flow bar still find it. The verdict's cause
 * and root cause always keep a row of their own.
 */

const LOG_PREFIX = 'student_page.journey.log';

/** Identical rows closer than this to the first of them become one row «×3». */
export const LOG_REPEAT_WINDOW_MS = 60 * 1000;
/** `Plugin Closed` / `Profile Logged Out` this close to `Session Ended` is the same exit. */
export const LOG_EXIT_MERGE_WINDOW_MS = 10 * 1000;
/** `App Opened` / `Plugin Opened` this close to a screen's start belongs to its header. */
const ENTRY_HEADER_WINDOW_MS = 2 * 1000;
/**
 * An `App Opened` / `Plugin Opened` screen shorter than this joins the next
 * chapter. admin-api no longer opens such screens; this keeps sessions recorded
 * before that change from starting with a 0-second «Тренинг» / «Справочник» chapter.
 */
export const ENTRY_SCREEN_MIN_DURATION_MS = 1000;

const ENTRY_EVENT_NAMES = new Set(['App Opened', 'Plugin Opened']);
const SESSION_ENDED_EVENT = 'Session Ended';

export type JourneyLogRow = {
  /**
   * Event the row renders. A task pair renders its result (icon, subtitle) under
   * the index of the task's start, so the row keeps the first index.
   */
  event: SessionEvent;
  /** Every event the row stands for, first index first (anchors and highlight). */
  eventIndexes: number[];
  /** «Решил задачу 1 из 5», «Открыл задачу 4 из 5 — не решил»; `null` = the usual title. */
  title: string | null;
  /** «за 49 с», «решал 2 мин 50 с», «думал 40 с». */
  note: string | null;
  /** How many identical rows this one stands for («×3» when above 1). */
  repeatCount: number;
  /** Events of the identical rows folded into this one (their request ids go to the developer). */
  foldedEvents: SessionEvent[];
  /** The whole catalog block repeats the chapter header. */
  hideCatalogContext: boolean;
  /** Lines of the catalog block the chapter header already says. */
  hideCatalogLines: { course: boolean; lesson: boolean };
  showGapBefore: boolean;
  /** Pause drawn above the row when `showGapBefore`. */
  gapBeforeMs: number;
};

export type JourneyLogChapter = {
  /**
   * The chapter's screen: label, identity and the section anchor (the first
   * screen, or the one after a 0-second entry screen that joined it).
   */
  screen: SessionScreen;
  screenIndexes: number[];
  label: string;
  offsetMs: number;
  durationMs: number;
  hasError: boolean;
  hasSlow: boolean;
  /** «решил 3 из 5»; `null` when the chapter had no tasks. */
  tasksSummary: string | null;
  /** The screen's own view and entry events: the header shows them, they stay anchors. */
  headerEventIndexes: number[];
  /** What those events add: the parent course, «Пришёл с google.com», «Первый запуск». */
  headerNotes: string[];
  /** Raw values of the header events, for «Технические детали» only. */
  headerTechnicalDetails: string[];
  /** Pause before the chapter's first event (drawn above the header), else `null`. */
  gapBeforeMs: number | null;
  rows: JourneyLogRow[];
  /** Non-blocking warnings hidden until «Технические детали» is on. */
  hiddenTechnicalEventIndexes: number[];
};

export type JourneyEventLogOptions = {
  techMode?: boolean;
  studentEmail?: string | null;
};

/* Chapters */

const CATALOG_SCREEN_KINDS: Record<string, string> = {
  trainer_viewed: 'trainer',
  training_viewed: 'training',
  arcade_viewed: 'arcade',
  course_viewed: 'course',
};

/**
 * Consecutive screens with the same key form one chapter: one lesson (by
 * `lesson_id`, else its name) or one trainer / training / arcade / course (by
 * `module_id ?? training_id ?? arcade_id ?? course_id`, else its name). Other
 * screens never merge.
 */
export const getScreenChapterKey = (
  screen: Pick<SessionScreen, 'eventName' | 'properties'>,
): string | null => {
  if (!screen.eventName) {
    return null;
  }

  const translationKey = eventNameToTranslationKey(screen.eventName);

  if (translationKey === 'lesson_viewed') {
    const lesson = readJourneyLessonIdentity(screen.properties);

    return lesson ? `lesson:${lesson}` : null;
  }

  const kind = CATALOG_SCREEN_KINDS[translationKey];
  const module = kind ? readJourneyModuleIdentity(screen.properties) : null;

  return kind && module ? `${kind}:${module}` : null;
};

const isSameChapter = (
  left: Pick<SessionScreen, 'eventName' | 'properties'>,
  right: Pick<SessionScreen, 'eventName' | 'properties'>,
) => {
  const key = getScreenChapterKey(right);

  return key != null && key === getScreenChapterKey(left);
};

/** A 0-second `App Opened` / `Plugin Opened` screen (sessions recorded before admin-api merged them). */
export const isShortEntryScreen = (
  screen: Pick<SessionScreen, 'eventName' | 'durationMs'>,
) =>
  screen.eventName != null &&
  ENTRY_EVENT_NAMES.has(getAnalyticsEventBaseName(screen.eventName)) &&
  screen.durationMs < ENTRY_SCREEN_MIN_DURATION_MS;

/** Position of the chapter's own screen: the first one that is not a short entry screen. */
const getPrimaryPosition = <T>(
  chapter: T[],
  getScreen: (item: T) => SessionScreen,
) =>
  Math.max(
    chapter.findIndex((item) => !isShortEntryScreen(getScreen(item))),
    0,
  );

/**
 * Consecutive screens of one lesson / trainer form a chapter; a short entry
 * screen (not the last one) joins the chapter that follows it.
 */
const groupIntoChapters = <T>(
  items: T[],
  getScreen: (item: T) => SessionScreen,
): T[][] => {
  const chapters: T[][] = [];
  let pendingEntries: T[] = [];

  items.forEach((item, position) => {
    const screen = getScreen(item);
    const chapter = chapters[chapters.length - 1];

    if (position < items.length - 1 && isShortEntryScreen(screen)) {
      pendingEntries.push(item);
      return;
    }

    if (
      pendingEntries.length === 0 &&
      chapter &&
      isSameChapter(
        getScreen(chapter[getPrimaryPosition(chapter, getScreen)]),
        screen,
      )
    ) {
      chapter.push(item);
    } else {
      chapters.push([...pendingEntries, item]);
    }

    pendingEntries = [];
  });

  return chapters;
};

/**
 * Screens of the flow bar, merged the same way as the log's chapters, so a
 * segment and a chapter always match (the segment jumps to the chapter's screen).
 */
export const buildChapterScreens = (
  screens: SessionScreen[],
): SessionScreen[] =>
  groupIntoChapters(screens, (screen) => screen).map((chapter) => {
    const primary = chapter[getPrimaryPosition(chapter, (screen) => screen)];
    const [first] = chapter;
    const last = chapter[chapter.length - 1];

    return {
      ...primary,
      startedAt: first.startedAt,
      firstEventIndex: first.firstEventIndex,
      lastEventIndex: last.lastEventIndex,
      durationMs: chapter.reduce(
        (sum, screen) => sum + Math.max(screen.durationMs, 0),
        0,
      ),
      hasError: chapter.some((screen) => screen.hasError),
      hasSlow: chapter.some((screen) => screen.hasSlow),
      hasGoal: chapter.some((screen) => screen.hasGoal),
    };
  });

/* Tasks */

type TaskRole = 'started' | 'completed' | 'skipped';

type TaskEvent = {
  flow: 'lesson' | 'intro';
  role: TaskRole;
  number: number;
  total: number | null;
  lessonId: string | null;
};

const TASK_EVENT_ROLES: Record<
  string,
  Pick<TaskEvent, 'flow' | 'role'> | undefined
> = {
  task_started: { flow: 'lesson', role: 'started' },
  task_completed: { flow: 'lesson', role: 'completed' },
  task_skipped: { flow: 'lesson', role: 'skipped' },
  plugin_intro_task_started: { flow: 'intro', role: 'started' },
  plugin_intro_task_completed: { flow: 'intro', role: 'completed' },
};

const TASK_ANSWER_EVENT_KEYS = new Set([
  'task_answered',
  'lesson_task_answered',
]);

const readPositiveNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : null;

const readTaskTotal = (properties: Record<string, unknown> | undefined) =>
  readPositiveNumber(properties?.task_total) ??
  readPositiveNumber(properties?.tasks_total);

const readTaskEvent = (
  event: Pick<SessionEvent, 'eventName' | 'properties'>,
): TaskEvent | null => {
  const role = TASK_EVENT_ROLES[eventNameToTranslationKey(event.eventName)];
  const number = readPositiveNumber(event.properties?.task_number);

  if (!role || number == null) {
    return null;
  }

  return {
    ...role,
    number,
    total: readTaskTotal(event.properties),
    lessonId: readJourneyCatalogId(event.properties?.lesson_id),
  };
};

/** Same flow and number; the lesson must match when both events name it. */
const isSameTask = (left: TaskEvent, right: TaskEvent) =>
  left.flow === right.flow &&
  left.number === right.number &&
  (!left.lessonId || !right.lessonId || left.lessonId === right.lessonId);

type TaskMatch = { event: SessionEvent; task: TaskEvent };

/** The next event of the same task anywhere later in the session (result, skip or re-open). */
const findNextTaskEvent = (
  allEvents: SessionEvent[],
  event: SessionEvent,
  task: TaskEvent,
): TaskMatch | null => {
  for (const candidate of allEvents) {
    const candidateTask =
      candidate.index > event.index ? readTaskEvent(candidate) : null;

    if (candidateTask && isSameTask(task, candidateTask)) {
      return { event: candidate, task: candidateTask };
    }
  }

  return null;
};

/** The start of this task, when the previous event of the same task is its start. */
const findTaskStart = (
  allEvents: SessionEvent[],
  event: SessionEvent,
  task: TaskEvent,
): TaskMatch | null => {
  for (let position = allEvents.length - 1; position >= 0; position -= 1) {
    const candidate = allEvents[position];
    const candidateTask =
      candidate.index < event.index ? readTaskEvent(candidate) : null;

    if (candidateTask && isSameTask(task, candidateTask)) {
      return candidateTask.role === 'started'
        ? { event: candidate, task: candidateTask }
        : null;
    }
  }

  return null;
};

/** «Решил задачу 3 из 5» / «Пропустил задачу 3 из 5»: the result borrows the total from the start. */
const getTaskResultTitle = (
  event: SessionEvent,
  task: TaskEvent,
  total: number | null,
) =>
  translateAnalyticsEvent(event.eventName, {
    taskNumber: task.number,
    taskTotal: total,
  });

/** «за 49 с» for a quick task, «решал 2 мин 50 с» when it took a pause-sized while. */
const getSolvingNote = (durationMs: number, adjacent: boolean) =>
  getTranslation(
    `${LOG_PREFIX}.${
      adjacent && durationMs < LOG_GAP_THRESHOLD_MS
        ? 'task_took'
        : 'task_solving'
    }`,
    { duration: formatDuration(durationMs) },
  );

/** «думал 40 с», unless the skip already says it (`time_spent_before_skip_sec`). */
const getSkipNote = (event: SessionEvent, durationMs: number) =>
  readPositiveNumber(event.properties?.time_spent_before_skip_sec) != null
    ? null
    : getTranslation(`${LOG_PREFIX}.task_thinking`, {
        duration: formatDuration(durationMs),
      });

/* Rows */

export type CollapseScreenEventsContext = {
  /** All events of the session: a task's result may come in a later chapter. */
  allEvents: SessionEvent[];
  /** Chapter screen: rows repeating its lesson / module lose the catalog block. */
  screen: Pick<SessionScreen, 'eventName' | 'firstEventIndex' | 'properties'>;
  studentEmail?: string | null;
  /** Hidden events a row carries as anchors, by the index of that row's event. */
  absorbedIndexesByEvent?: Map<number, number[]>;
  /** The verdict's cause and root cause: never folded into a row, nothing folded into them. */
  pinned?: Set<number>;
  /** «Технические детали»: error, slow and rage rows are never folded, each shows its own details. */
  techMode?: boolean;
  /** Pause to draw above a row whose screen's view moved into the chapter header. */
  gapBeforeByEvent?: Map<number, number>;
};

const createRow = (
  event: SessionEvent,
  eventIndexes: number[],
  overrides: Partial<Pick<JourneyLogRow, 'title' | 'note'>> = {},
): JourneyLogRow => ({
  event,
  eventIndexes,
  title: overrides.title ?? null,
  note: overrides.note ?? null,
  repeatCount: 1,
  foldedEvents: [],
  hideCatalogContext: false,
  hideCatalogLines: { course: false, lesson: false },
  showGapBefore: false,
  gapBeforeMs: event.gapBeforeMs,
});

/** Started + its result right after it → one row at the result's time, with the start's index. */
const createTaskPairRow = (
  started: TaskMatch,
  finished: TaskMatch,
  eventIndexes: number[],
): JourneyLogRow => {
  const durationMs = Math.max(
    finished.event.offsetMs - started.event.offsetMs,
    0,
  );

  return createRow(
    {
      ...finished.event,
      index: started.event.index,
      gapBeforeMs: started.event.gapBeforeMs,
    },
    eventIndexes,
    {
      title: getTaskResultTitle(
        finished.event,
        finished.task,
        started.task.total ?? finished.task.total,
      ),
      note:
        finished.task.role === 'completed'
          ? getSolvingNote(durationMs, true)
          : getSkipNote(finished.event, durationMs),
    },
  );
};

/** A task's start or result that is not merged with its pair. */
const createTaskRow = (
  event: SessionEvent,
  task: TaskEvent,
  eventIndexes: number[],
  allEvents: SessionEvent[],
): JourneyLogRow => {
  if (task.role === 'started') {
    const next = findNextTaskEvent(allEvents, event, task);

    return createRow(event, eventIndexes, {
      title: next
        ? null
        : getTranslation(`${LOG_PREFIX}.task_not_solved`, {
            title: translateAnalyticsEvent(event.eventName, {
              taskNumber: task.number,
              taskTotal: task.total,
            }),
          }),
    });
  }

  const start = findTaskStart(allEvents, event, task);

  if (!start) {
    return createRow(event, eventIndexes);
  }

  const durationMs = Math.max(event.offsetMs - start.event.offsetMs, 0);

  return createRow(event, eventIndexes, {
    title: getTaskResultTitle(event, task, start.task.total ?? task.total),
    note:
      task.role === 'completed'
        ? getSolvingNote(durationMs, false)
        : getSkipNote(event, durationMs),
  });
};

/** Rows of these kinds carry details of their own (error card, timing), shown per row in tech mode. */
const DIAGNOSTIC_KINDS = new Set<EventKind>([
  'error',
  'warning',
  'slow',
  'rage',
]);

const readSignatureValue = (value: unknown) =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean'
    ? value
    : null;

/**
 * What makes two errors the same error: the request (method + route template),
 * the response, the crash kind, name, message and stack. The request id and the
 * timing differ on every retry, so they do not count.
 */
const getErrorSignatureDetails = (event: SessionEvent) => {
  const properties = event.properties ?? {};
  const endpoint =
    readSignatureValue(properties.endpoint) ??
    readSignatureValue(event.serverError?.endpoint);
  const { message, stack } = getErrorDiagnostics(event);

  return [
    properties.method,
    typeof endpoint === 'string' ? normalizeEndpointTemplate(endpoint) : null,
    properties.status ?? event.serverError?.status,
    properties.error_code,
    properties.error_kind,
    properties.error_name ?? event.serverError?.errorName,
    properties.fatal,
    properties.thread,
    properties.message_type,
    message,
    stack,
  ].map(readSignatureValue);
};

/**
 * Rows that count as «the same» for «×3»: same kind, title and subtitle; errors
 * compare by their diagnostics (two different crashes never fold into one row).
 */
const getRepeatSignature = (
  row: JourneyLogRow,
  studentEmail?: string | null,
) => {
  const display = formatAnalyticsEventDisplay(
    row.event.eventName,
    row.event.properties,
    {
      studentEmail,
      platform: row.event.platform,
      omitCatalog: row.hideCatalogContext,
    },
  );
  const isErrorLike =
    row.event.kind === 'error' || row.event.kind === 'warning';
  const details = isErrorLike
    ? getErrorSignatureDetails(row.event)
    : [display.subtitle, row.note];

  return JSON.stringify([
    row.event.kind,
    row.event.eventName,
    row.title ?? display.title,
    ...details,
  ]);
};

/**
 * Rows of one chapter: «Задача N начата» + «Задача N выполнена» → «Решил задачу N
 * из M · за 49 с»; a start without any result later → «Открыл задачу N из M — не
 * решил»; identical rows within 60 s of the first → one row «×3» at the first
 * one's time (never the verdict's cause or root cause, never different errors,
 * never error rows in tech mode). `events` are the chapter's visible events in order.
 */
export const collapseScreenEvents = (
  events: SessionEvent[],
  context: CollapseScreenEventsContext,
): JourneyLogRow[] => {
  const {
    allEvents,
    screen,
    studentEmail,
    absorbedIndexesByEvent,
    pinned,
    techMode = false,
    gapBeforeByEvent,
  } = context;
  const canFold = (row: JourneyLogRow) =>
    !row.eventIndexes.some((index) => pinned?.has(index)) &&
    !(techMode && DIAGNOSTIC_KINDS.has(row.event.kind));
  const indexesOf = (event: SessionEvent) => [
    event.index,
    ...(absorbedIndexesByEvent?.get(event.index) ?? []),
  ];
  const rows: JourneyLogRow[] = [];

  for (let position = 0; position < events.length; position += 1) {
    const event = events[position];
    const task = readTaskEvent(event);
    const next = events[position + 1];
    const nextTask = next ? readTaskEvent(next) : null;

    if (
      task?.role === 'started' &&
      next &&
      nextTask &&
      nextTask.role !== 'started' &&
      isSameTask(task, nextTask)
    ) {
      rows.push(
        createTaskPairRow({ event, task }, { event: next, task: nextTask }, [
          ...indexesOf(event),
          ...indexesOf(next),
        ]),
      );
      position += 1;
    } else if (task) {
      rows.push(createTaskRow(event, task, indexesOf(event), allEvents));
    } else {
      rows.push(createRow(event, indexesOf(event)));
    }
  }

  return rows
    .map((row) => {
      const redundancy = getEventCatalogRedundancy(row.event, screen);
      const gapOverride = gapBeforeByEvent?.get(row.event.index);

      return {
        ...row,
        hideCatalogContext: redundancy.all,
        hideCatalogLines: {
          course: redundancy.course,
          lesson: redundancy.lesson,
        },
        showGapBefore: gapOverride != null || shouldShowGapBefore(row.event),
        gapBeforeMs: gapOverride ?? row.event.gapBeforeMs,
      };
    })
    .reduce<Array<{ row: JourneyLogRow; signature: string }>>((merged, row) => {
      const signature = getRepeatSignature(row, studentEmail);
      const previous = merged[merged.length - 1];

      if (
        previous &&
        previous.signature === signature &&
        canFold(previous.row) &&
        canFold(row) &&
        row.event.offsetMs - previous.row.event.offsetMs <= LOG_REPEAT_WINDOW_MS
      ) {
        previous.row = {
          ...previous.row,
          eventIndexes: [...previous.row.eventIndexes, ...row.eventIndexes],
          repeatCount: previous.row.repeatCount + row.repeatCount,
          foldedEvents: [
            ...previous.row.foldedEvents,
            row.event,
            ...row.foldedEvents,
          ],
        };
      } else {
        merged.push({ row, signature });
      }

      return merged;
    }, [])
    .map(({ row }) => row);
};

/* Session-level merges */

/** The verdict's cause and root cause are never hidden or folded into a header. */
const getPinnedEventIndexes = (
  detail: Pick<AnalyticsStudentSessionDetailDto, 'events' | 'exit'>,
): Set<number> => {
  const causeEvent =
    detail.exit?.causeEventIndex != null
      ? detail.events.find(
          (event) => event.index === detail.exit.causeEventIndex,
        ) ?? null
      : null;
  const rootCause = causeEvent
    ? findErrorRootCause(detail.events, causeEvent)
    : null;

  return new Set(
    [causeEvent?.index, rootCause?.index].filter(
      (index): index is number => index != null,
    ),
  );
};

/**
 * `Plugin Closed` (or `Profile Logged Out` before a logout) followed by
 * `Session Ended` within 10 s is one exit: hidden index → the exit row's index.
 */
export const findMergedExitEvents = (
  events: SessionEvent[],
  pinned: Set<number> = new Set(),
): Map<number, number> => {
  const merged = new Map<number, number>();

  events.forEach((event, position) => {
    const baseName = getAnalyticsEventBaseName(event.eventName);

    if (
      pinned.has(event.index) ||
      (baseName !== 'Plugin Closed' && baseName !== 'Profile Logged Out')
    ) {
      return;
    }

    const exit = events
      .slice(position + 1)
      .find(
        (candidate) =>
          candidate.offsetMs - event.offsetMs <= LOG_EXIT_MERGE_WINDOW_MS &&
          getAnalyticsEventBaseName(candidate.eventName) ===
            SESSION_ENDED_EVENT,
      );

    if (
      exit &&
      (baseName === 'Plugin Closed' || exit.properties?.reason === 'logout')
    ) {
      merged.set(event.index, exit.index);
    }
  });

  return merged;
};

const invertMerges = (merges: Map<number, number>) => {
  const byTarget = new Map<number, number[]>();

  merges.forEach((target, hidden) => {
    byTarget.set(target, [...(byTarget.get(target) ?? []), hidden]);
  });

  return byTarget;
};

/* Chapter header */

const normalizeText = (value: string) => value.toLowerCase().replace(/ё/g, 'е');

/**
 * The screen's own view event and the entry events of its first seconds
 * (`App Opened` / `Plugin Opened`): the header already says when the screen opened.
 */
const findHeaderEvents = (
  group: ScreenEventGroup,
  pinned: Set<number>,
  isFirstGroup: boolean,
): SessionEvent[] => {
  const screenBaseName = group.screen.eventName
    ? getAnalyticsEventBaseName(group.screen.eventName)
    : null;
  const startMs = group.events[0]?.offsetMs ?? group.offsetMs;
  const headerEvents: SessionEvent[] = [];
  let hasOwnView = false;

  for (const event of group.events) {
    const baseName = getAnalyticsEventBaseName(event.eventName);
    const isOwnView: boolean = !hasOwnView && baseName === screenBaseName;
    const isEntry =
      isFirstGroup &&
      ENTRY_EVENT_NAMES.has(baseName) &&
      event.offsetMs - startMs <= ENTRY_HEADER_WINDOW_MS;

    if (event.kind !== 'view' || pinned.has(event.index)) {
      break;
    }

    if (!isOwnView && !isEntry) {
      break;
    }

    hasOwnView = hasOwnView || isOwnView;
    headerEvents.push(event);
  }

  return headerEvents;
};

/** Parent course, «Пришёл с google.com», «Первый запуск» — without what the title already says. */
const buildHeaderNotes = (
  label: string,
  headerEvents: SessionEvent[],
  { techMode, studentEmail }: JourneyEventLogOptions,
) => {
  const notes: string[] = [];
  const subtitles: string[] = [];
  const technicalDetails: string[] = [];
  const normalizedLabel = normalizeText(label);
  const addUnique = (list: string[], value: string | null | undefined) => {
    if (
      value &&
      !normalizedLabel.includes(normalizeText(value)) &&
      ![...notes, ...subtitles].includes(value)
    ) {
      list.push(value);
    }
  };

  headerEvents.forEach((event) => {
    const display = formatAnalyticsEventDisplay(
      event.eventName,
      event.properties,
      { studentEmail, platform: event.platform },
    );

    addUnique(notes, display.catalogContext?.courseLine);
    addUnique(notes, display.catalogContext?.moduleLine);
    addUnique(subtitles, display.subtitle);

    if (techMode && display.technicalDetails) {
      technicalDetails.push(display.technicalDetails);
    }
  });

  return { notes: [...notes, ...subtitles], technicalDetails };
};

/* Solved tasks: one counter for chapter headers and the session story */

export type SolvedTasksCount = {
  /** Distinct solved tasks (per lesson, at least `tasks_done` of its Lesson Completed). */
  solved: number;
  /** Largest `tasks_total` / `task_total` seen, else `null`. */
  total: number | null;
  /** The events include tasks at all (a start, a result or a graded answer). */
  hasTasks: boolean;
};

/**
 * Solved tasks, the same way everywhere: server results and correct web answers,
 * one per lesson + task number. Web lessons answered as a whole (Difference,
 * TwoColumns, ConnectionQuiz) send no number — one correct answer is one task of
 * that lesson; an answer with neither a grade nor a number (ColorMatch) is no task.
 * A Lesson Completed alone is no task (theory lessons complete too).
 */
export const countSolvedTasks = (
  events: Array<Pick<SessionEvent, 'eventName' | 'properties'>>,
  { includeIntro = true }: { includeIntro?: boolean } = {},
): SolvedTasksCount => {
  const solvedByLesson = new Map<string, Set<string>>();
  const doneByLesson = new Map<string, number>();
  let total: number | null = null;
  let hasTasks = false;

  const markSolved = (lessonKey: string, taskKey: string) => {
    solvedByLesson.set(
      lessonKey,
      new Set([...(solvedByLesson.get(lessonKey) ?? []), taskKey]),
    );
  };

  events.forEach((event, position) => {
    const properties = event.properties ?? {};
    const translationKey = eventNameToTranslationKey(event.eventName);
    const role = TASK_EVENT_ROLES[translationKey];
    const lessonId = readJourneyCatalogId(properties.lesson_id);
    const eventTotal = readTaskTotal(properties);

    if (role?.flow === 'intro' && !includeIntro) {
      return;
    }

    if (eventTotal != null) {
      total = Math.max(total ?? 0, eventTotal);
    }

    if (role) {
      const number = readPositiveNumber(properties.task_number);

      hasTasks = true;

      if (role.role === 'completed') {
        markSolved(
          `${role.flow}:${lessonId ?? ''}`,
          number != null ? `n${number}` : `#${position}`,
        );
      }

      return;
    }

    if (TASK_ANSWER_EVENT_KEYS.has(translationKey)) {
      /* The web numbers answers with `task_index` (1-based, as shown in the subtitle). */
      const number =
        readPositiveNumber(properties.task_index) ??
        readPositiveNumber(properties.task_number);

      if (typeof properties.is_correct !== 'boolean' && number == null) {
        return;
      }

      hasTasks = true;

      if (properties.is_correct === true) {
        markSolved(
          `lesson:${lessonId ?? ''}`,
          number != null ? `n${number}` : lessonId ? 'lesson' : `#${position}`,
        );
      }

      return;
    }

    const tasksDone = readPositiveNumber(properties.tasks_done);

    if (translationKey === 'lesson_completed' && tasksDone != null) {
      const lessonKey = `lesson:${lessonId ?? ''}`;

      doneByLesson.set(
        lessonKey,
        Math.max(doneByLesson.get(lessonKey) ?? 0, tasksDone),
      );
    }
  });

  const solved = [
    ...new Set([...solvedByLesson.keys(), ...doneByLesson.keys()]),
  ].reduce(
    (sum, lessonKey) =>
      sum +
      Math.max(
        solvedByLesson.get(lessonKey)?.size ?? 0,
        doneByLesson.get(lessonKey) ?? 0,
      ),
    0,
  );

  return { solved, total, hasTasks: hasTasks || solved > 0 };
};

/** «решил 3 из 5» / «решил 3 задачи» / «не решил ни одной из 4»; `null` without tasks. */
export const getChapterTasksSummary = (
  events: Array<Pick<SessionEvent, 'eventName' | 'properties'>>,
): string | null => {
  const { solved, total, hasTasks } = countSolvedTasks(events);

  if (!hasTasks) {
    return null;
  }

  if (total != null) {
    return solved > 0
      ? getTranslation(`${LOG_PREFIX}.tasks_of_total`, {
          solved: Math.min(solved, total),
          total,
        })
      : getTranslation(`${LOG_PREFIX}.tasks_none_of_total`, { total });
  }

  return solved > 0
    ? getTranslation(`${LOG_PREFIX}.tasks_solved`, { count: solved })
    : getTranslation(`${LOG_PREFIX}.tasks_none`);
};

/* The log */

/**
 * Where to draw «N мин без событий» inside a chapter: before the first row after
 * the view of a later screen that moved into the header (its pause would be lost
 * otherwise). The pause runs from the last event before that view to the row.
 */
const findFoldedHeaderGaps = (
  events: SessionEvent[],
  headerIndexes: Set<number>,
  isListed: (event: SessionEvent) => boolean,
): Map<number, number> => {
  const gaps = new Map<number, number>();
  let pauseStartMs: number | null = null;

  events.forEach((event, position) => {
    if (headerIndexes.has(event.index)) {
      if (position > 0 && pauseStartMs == null && shouldShowGapBefore(event)) {
        pauseStartMs = events[position - 1].offsetMs;
      }

      return;
    }

    if (pauseStartMs != null && isListed(event)) {
      gaps.set(event.index, Math.max(event.offsetMs - pauseStartMs, 0));
      pauseStartMs = null;
    }
  });

  return gaps;
};

/**
 * Chapters of the session log. `techMode` shows non-blocking 4xx rows, unfolds
 * repeated error rows and the raw values of header events; everything else is
 * the same in both modes.
 */
export const buildJourneyEventLog = (
  detail: Pick<
    AnalyticsStudentSessionDetailDto,
    'screens' | 'events' | 'startedAt' | 'exit'
  >,
  options: JourneyEventLogOptions = {},
): JourneyLogChapter[] => {
  const { techMode = false, studentEmail } = options;
  const pinned = getPinnedEventIndexes(detail);
  const exitMerges = findMergedExitEvents(detail.events, pinned);
  const absorbedIndexesByEvent = invertMerges(exitMerges);
  const isHiddenTechnical = (event: SessionEvent) =>
    !techMode && event.kind === 'warning' && !pinned.has(event.index);
  const getScreen = (group: ScreenEventGroup) => group.screen;

  return groupIntoChapters(groupEventsByScreen(detail), getScreen).map(
    (groups) => {
      const primaryPosition = getPrimaryPosition(groups, getScreen);
      const primary = groups[primaryPosition];
      const [firstGroup] = groups;
      const events = groups.flatMap((group) => group.events);
      /* Entry events (`Plugin Opened`) join the header up to the chapter's own screen. */
      const headerEventsByGroup = groups.map((group, position) =>
        findHeaderEvents(group, pinned, position <= primaryPosition),
      );
      const headerIndexes = new Set(
        headerEventsByGroup.flat().map((event) => event.index),
      );
      const { notes, technicalDetails } = buildHeaderNotes(
        primary.label,
        headerEventsByGroup.slice(0, primaryPosition + 1).flat(),
        options,
      );
      const listed = events.filter(
        (event) =>
          !headerIndexes.has(event.index) && !exitMerges.has(event.index),
      );
      const isRow = (event: SessionEvent) =>
        !headerIndexes.has(event.index) &&
        !exitMerges.has(event.index) &&
        !isHiddenTechnical(event);
      const firstEvent = events[0];

      return {
        screen: primary.screen,
        screenIndexes: groups.map((group) => group.screen.index),
        label: primary.label,
        offsetMs: firstGroup.offsetMs,
        durationMs: groups.reduce(
          (sum, group) => sum + Math.max(group.screen.durationMs, 0),
          0,
        ),
        hasError: groups.some((group) => group.screen.hasError),
        hasSlow: groups.some((group) => group.screen.hasSlow),
        tasksSummary: getChapterTasksSummary(events),
        headerEventIndexes: [...headerIndexes],
        headerNotes: notes,
        headerTechnicalDetails: technicalDetails,
        gapBeforeMs:
          firstEvent &&
          headerIndexes.has(firstEvent.index) &&
          shouldShowGapBefore(firstEvent)
            ? firstEvent.gapBeforeMs
            : null,
        rows: collapseScreenEvents(listed.filter(isRow), {
          allEvents: detail.events,
          screen: primary.screen,
          studentEmail,
          absorbedIndexesByEvent,
          pinned,
          techMode,
          gapBeforeByEvent: findFoldedHeaderGaps(events, headerIndexes, isRow),
        }),
        hiddenTechnicalEventIndexes: listed
          .filter(isHiddenTechnical)
          .map((event) => event.index),
      };
    },
  );
};
